/**
 * Cross-tab event bus for fact-check completion notifications.
 * Uses BroadcastChannel (same-origin only) to propagate events across browser tabs.
 */

const CHANNEL_NAME = "fact-check-events"

export type CrossTabFactCheckEvent = {
  eventId: string
  originTabId: string
  type: "completed" | "error"
  sessionType: string
  sessionId?: string
  messageId?: string
  completedAt: number
  errorMessage?: string
}

type CrossTabListener = (event: CrossTabFactCheckEvent) => void

let channel: BroadcastChannel | null = null
const listeners = new Set<CrossTabListener>()

function getTabId(): string {
  let tabId = sessionStorage.getItem("fact-check-tab-id")
  if (!tabId) {
    tabId = crypto.randomUUID()
    sessionStorage.setItem("fact-check-tab-id", tabId)
  }
  return tabId
}

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null
  }
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = (event: MessageEvent<CrossTabFactCheckEvent>) => {
      const data = event.data
      if (data.originTabId === getTabId()) {
        return
      }
      for (const listener of listeners) {
        try {
          listener(data)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error in cross-tab fact-check listener", error)
        }
      }
    }
  }
  return channel
}

export function publishCrossTabEvent(
  event: Omit<CrossTabFactCheckEvent, "originTabId" | "eventId">
): void {
  const ch = getChannel()
  if (!ch) return

  const fullEvent: CrossTabFactCheckEvent = {
    ...event,
    eventId: crypto.randomUUID(),
    originTabId: getTabId(),
  }
  ch.postMessage(fullEvent)
}

export function subscribeCrossTab(listener: CrossTabListener): () => void {
  getChannel()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
